/**
 * Hook para gerenciamento dos filtros de indicações.
 *
 * Encapsula o acesso aos filtros do store e expõe setters individuais
 * para uso nos componentes de UI (FilterPanel, etc).
 *
 * @module hooks/useFilters
 */

import { useCallback } from "react";
import useStore from "../store/indicacoesSlice";

/**
 * Fornece os filtros ativos e funções para atualizá-los.
 *
 * Cada setter dispara automaticamente o reload de pins e indicações
 * via store.setFilters().
 *
 * @returns {{
 *   filters: import("../store/indicacoesSlice").Filters,
 *   setAno: (ano: number|null) => Promise<void>,
 *   setAutor: (autor: string|null) => Promise<void>,
 *   setThematicCategory: (thematicCategory: string|null) => Promise<void>,
 *   setBairro: (bairro: string|null) => Promise<void>,
 *   setSearch: (search: string|null) => Promise<void>,
 *   setAproximado: (aproximado: boolean|null) => Promise<void>,
 *   resetFilters: () => Promise<void>,
 *   hasActiveFilters: boolean
 * }}
 */
export function useFilters() {
  const filters = useStore((s) => s.filters);
  const setFilters = useStore((s) => s.setFilters);
  const resetFiltersAction = useStore((s) => s.resetFilters);

  const setAno = useCallback(
    /** @param {number|null} ano */
    (ano) => setFilters({ ano }),
    [setFilters]
  );

  const setAutor = useCallback(
    /** @param {string|null} autor */
    (autor) => setFilters({ autor }),
    [setFilters]
  );

  const setThematicCategory = useCallback(
    /** @param {string|null} thematicCategory */
    (thematicCategory) => setFilters({ thematic_category: thematicCategory }),
    [setFilters]
  );

  const setBairro = useCallback(
    /**
     * @param {string|null} bairro
     * @param {number|null} [bairroId]
     */
    (bairro, bairroId = null) => {
      if (bairro === null) {
        setFilters({ bairro: null, bairro_id: null });
      } else {
        setFilters({ bairro, bairro_id: bairroId });
      }
    },
    [setFilters]
  );

  const setBairroId = useCallback(
    /**
     * @param {number|null} bairroId
     * @param {string|null} [bairroName]
     */
    (bairroId, bairroName = null) => {
      setFilters({ bairro_id: bairroId, bairro: bairroName });
    },
    [setFilters]
  );

  const setSearch = useCallback(
    /** @param {string|null} search */
    (search) => setFilters({ search: search || null }),
    [setFilters]
  );

  const setAproximado = useCallback(
    /** @param {boolean|null} aproximado */
    (aproximado) => setFilters({ aproximado }),
    [setFilters]
  );

  const hasActiveFilters =
    filters.ano !== null ||
    (Array.isArray(filters.anos) && filters.anos.length > 0) ||
    filters.autor !== null ||
    filters.thematic_category !== null ||
    filters.bairro !== null ||
    filters.bairro_id !== null ||
    filters.search !== null ||
    filters.aproximado !== null;

  return {
    filters,
    applyFilters: setFilters,
    setAno,
    setAutor,
    setThematicCategory,
    setBairro,
    setBairroId,
    setSearch,
    setAproximado,
    resetFilters: resetFiltersAction,
    hasActiveFilters,
  };
}
